import { track } from "@src/utils/analytics"
import { useCallback, useContext, useEffect, useState } from "react"
import useSWR from "swr"

import { getLambdaRoute } from "../../utils/getLambdaRoute"
import { VisitorLocaleContext } from "../providers/VisitorLocaleProvider"
import { useLiveTemplatesData } from "./useLiveTemplateData"

/**
 * @param {string[]} templatePermaKeys - the karbon unique key(s) for the template being worked upon
 * @param {string} slug - the template slug that's being added
 * @description hook to build adder function and return some states about progress
 * @return {function} add - Call this to do the add to Karbon call
 * @return {boolean} adding - true while the add fuction is resolving
 * @return {string} successURL the Karbon app deeplink where the new template can bee seen
 * @return {Error} error error thrown if add() fails
 * @return {string[]} failedTemplates - the Karbon unique titles for the templates that failed to be added in Karbon
 */

export const multiAddError =
  "Some templates have failed to be added to Karbon during the multi-import process"

type addToKarbonFunction = (
  templatePermaKeys: string[],
  slug: string
) => {
  add: (...args: string[]) => void
  adding: boolean
  successURL: string
  error: Error
  failedTemplates: string[]
}

// Basic SWR fetcher to get all templates data

const fetcher = url =>
  fetch(url)
    .then(res => res.json())
    .then(result => {
      return result
    })

export const useAddtoKarbon: addToKarbonFunction = (
  templatePermaKeys,
  slug
) => {
  const templates = useLiveTemplatesData()
  const visitorLocale = useContext(VisitorLocaleContext)
  const [successURL, setSuccessURL] = useState<string | undefined>()
  const [error, setError] = useState<TypeError | undefined>()
  const [adding, setAdding] = useState<boolean | undefined>()
  const [failedPermaKeys, setFailedPermaKeys] = useState([])
  const [failedTemplates, setFailedTemplates] = useState([])

  const { data: allTemplates } = useSWR(getLambdaRoute("summary"), fetcher)

  const [queue, setQueue] = useState(null)
  const doAdd = useCallback(async () => {
    const importErrors = []

    // @ts-expect-error
    const userData = visitorLocale.karbonData.data
    setAdding(true)
    setError(null)

    const requestData =
      templatePermaKeys.length === 1
        ? JSON.stringify({
            workTemplateImport: {
              userProfilePermaKey: userData.userProfilePermaKey,
              tenantPermaKey: userData.tenantPermaKey,
              workTemplatePermaKey: templatePermaKeys[0],
            },
          })
        : JSON.stringify({
            workTemplatesImport: {
              userProfilePermaKey: userData.userProfilePermaKey,
              tenantPermaKey: userData.tenantPermaKey,
              workTemplatePermaKeys: templatePermaKeys,
            },
          })

    const reportError = e => {
      setAdding(false)
      setError(e)

      const payload =
        templatePermaKeys.length === 1
          ? {
              userData: userData,
              workTemplateImport: {
                userProfilePermaKey: userData.userProfilePermaKey,
                tenantPermaKey: userData.tenantPermaKey,
                workTemplatePermaKey: templatePermaKeys[0],
              },
              error: e?.message,
            }
          : {
              userData: userData,
              workTemplatesImport: {
                userProfilePermaKey: userData.userProfilePermaKey,
                tenantPermaKey: userData.tenantPermaKey,
                workTemplatePermaKeys: templatePermaKeys,
                importWorkTemplateErrors: importErrors,
              },
              error: e?.message,
            }

      const templateConfigObj = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      }

      // Send to lambda route so that we can be notified of the failure
      fetch(getLambdaRoute(`template-error`), templateConfigObj).catch(
        error => {
          console.log(error)
        }
      )

      track(`WE-TemplateAddedFailure`, {
        label: slug,
        templatePermaKey: failedPermaKeys,
      })
    }

    try {
      if (!templatePermaKeys) {
        const tpkError = new TypeError("templatePermaKey not supplied!")
        reportError(tpkError)
        setAdding(false)
      }

      if (!userData.dataCenterUrl) {
        const dataCenterUrlError = new TypeError("dataCenterUrl not supplied!")
        reportError(dataCenterUrlError)
        setAdding(false)
      }

      // different endpoints used depending on if it's a multi-add or a single add

      const endpoint =
        templatePermaKeys.length === 1
          ? `workTemplatesImport`
          : `workTemplatesMultiImport`

      const response = await fetch(
        `${userData.dataCenterUrl}/work/api/${userData.tenantPermaKey}/${endpoint}`,
        {
          method: "POST",
          body: requestData,
          redirect: "follow",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      )

      if (response.ok) {
        track(`WE-TemplateAdded`, {
          label: slug,
        })

        const body = await response.json()

        // if a single add, then the user will be linked directly to that template in Karbon
        if (templatePermaKeys.length === 1) {
          /*
           body = {
             workTemplateLink: "https://stage.karbonhq.com/KWfygPgepa#/work-templates/36lGRVSdHykN"
           }
         */
          setSuccessURL(body.workTemplateLink)
        } else {
          /*
           * body = {
           * workTemplatesImport: {
           *   id: "7bd0e261-a688-4dc0-b2ec-3aaa2b0f8ed4",
           *   importWorkTemplateLinks:[
           *     {workTemplatePermaKey: "361D9SN4VMFz", location:"https://bl.local.com/26mnhJjXX3M#/work-templates/3k3Y1mhzYWqW"},
           *     {workTemplatePermaKey:"2lSNW3YwkXZL", location:"https://bl.local.com/26mnhJjXX3M#/work-templates/3t63hrBbPYGR"}
           *   ],
           *   importWorkTemplateErrors:[
           *     {"workTemplatePermaKey":"2lSNW3YwkXZL","error":"Not Found"}
           *   ]
           * }
           * }
           *
           * */

          if (body.workTemplatesImport.importWorkTemplateErrors.length) {
            // record entire error array so we can be notified of which things & why they went wrong
            importErrors.push(
              ...body.workTemplatesImport.importWorkTemplateErrors
            )
            // record PermaKeys for Segment tracking purposes
            const failedKeys = []
            body.workTemplatesImport.importWorkTemplateErrors.forEach(error =>
              failedKeys.push(error.workTemplatePermaKey)
            )
            setFailedPermaKeys([...failedPermaKeys, ...failedKeys])

            const partialImportError = new Error(multiAddError)

            reportError(partialImportError)
          }
          // if a multi add, then the user will be linked to the templates list in Karbon
          setSuccessURL(
            `${userData.dataCenterUrl}/${userData.tenantPermaKey}#/work-templates`
          )
        }
      } else {
        // error seems to just return a status code.
        const respError = new Error(`${response.status}`)
        // all of the permakeys will have failed, so just stick them all in there :'(
        setFailedPermaKeys([...failedPermaKeys, ...templatePermaKeys])
        reportError(respError)
      }

      setAdding(false)
    } catch (error) {
      reportError(error)
    }
  }, [visitorLocale.karbonData.data, templatePermaKeys, slug, failedPermaKeys])

  const add = useCallback(() => {
    setQueue(templatePermaKeys)
  }, [templatePermaKeys])

  useEffect(() => {
    if (queue) {
      setAdding(true)
    }

    if (queue && templates.data) {
      doAdd()
      setQueue(null)
    }
  }, [queue, templates.data, doAdd])

  useEffect(() => {
    // in the case of a failure, then provide the failed template name so that it can be used by the error modal
    const failedTitles = failedPermaKeys.map(permaKey =>
      allTemplates.find(template => template.id === permaKey)
    )
    setFailedTemplates(failedTitles)
  }, [failedPermaKeys, allTemplates])

  return { add, adding, successURL, error, failedTemplates }
}
